import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Building2, ShieldAlert, Mail, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const SECTIONS = [
  { id: "company", label: "Company", icon: Building2, keys: [
    "company_legal_name", "company_kra_pin",
    "company_address", "company_support_email", "company_support_phone",
  ]},
  { id: "policy", label: "Policy", icon: FileText, keys: [
    "cooling_off_days", "default_claim_limit_kes", "premium_grace_days",
    "auto_suspend_on_premium_failure",
    "tier_merchant_lite_kes", "tier_social_vendor_pro_kes", "tier_enterprise_sme_kes",
  ]},
  { id: "fraud", label: "Fraud thresholds", icon: ShieldAlert, keys: [
    "fraud_score_review_threshold", "fraud_score_escalate_threshold",
    "anomaly_high_severity_threshold", "anomaly_critical_severity_threshold",
  ]},
  { id: "email", label: "Email templates", icon: Mail, keys: [
    "email_from_name", "email_daily_digest_subject_prefix",
    "email_weekly_merchant_subject_prefix", "email_claim_status_subject_prefix",
    "email_kyc_status_subject_prefix", "email_payment_confirmation_subject_prefix",
  ]},
  { id: "webhook", label: "Webhooks", icon: ShieldAlert, keys: [
    "webhook_retry_max_attempts", "webhook_signing_window_seconds",
  ]},
];

function inferType(key, val) {
  if (typeof val === "boolean") return "bool";
  if (typeof val === "number") return "number";
  return "string";
}

function prettyLabel(k) {
  return k.replace(/_/g, " ")
    .replace(/\bkes\b/i, "(KES)")
    .replace(/\bkyc\b/i, "KYC")
    .replace(/^./, (c) => c.toUpperCase());
}

export default function SystemSettingsCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get("/admin/system-settings");
      setData(r.data);
      setDraft({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <section className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="system-settings-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#114232]" />
      </section>
    );
  }

  const settings = data.settings;
  const readOnly = new Set(data.read_only_keys || []);

  function update(k, raw) {
    const t = inferType(k, settings[k]);
    let v = raw;
    if (t === "number") v = raw === "" ? "" : Number(raw);
    if (t === "bool") v = !!raw;
    setDraft({ ...draft, [k]: v });
  }

  function value(k) {
    return draft[k] !== undefined ? draft[k] : settings[k];
  }

  async function save() {
    setSaving(true);
    try {
      const body = { ...draft };
      for (const k of Object.keys(body)) {
        if (body[k] === "" || body[k] === settings[k]) delete body[k];
      }
      if (Object.keys(body).length === 0) {
        toast.info("No changes to save");
        return;
      }
      const r = await api.patch("/admin/system-settings", body);
      toast.success(`Updated ${r.data.updated_keys.length} setting(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white border border-[#114232]/10 rounded-md p-6 space-y-6"
              data-testid="system-settings-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <Building2 className="w-5 h-5 text-[#c15337]" />
            <h2 className="font-heading text-lg font-bold text-[#114232]">System settings</h2>
          </div>
          <p className="text-xs text-[#5c6360] mt-1">
            Super-admin only. Changes apply to all merchants and admin users.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving || Object.keys(draft).length === 0}
          data-testid="system-settings-save"
          className="bg-[#c15337] hover:bg-[#a3432a]"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save changes ({Object.keys(draft).length})
        </Button>
      </div>

      {SECTIONS.map((sec) => (
        <div key={sec.id} className="space-y-3" data-testid={`settings-section-${sec.id}`}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-[#c15337]">
            <sec.icon className="w-3.5 h-3.5" /> {sec.label}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sec.keys.map((k) => {
              const t = inferType(k, settings[k]);
              const isRo = readOnly.has(k);
              if (t === "bool") {
                return (
                  <label
                    key={k}
                    className="flex items-center gap-2 border border-[#114232]/10 rounded-md p-3 cursor-pointer hover:bg-[#114232]/3"
                    data-testid={`settings-field-${k}`}
                  >
                    <input
                      type="checkbox" checked={!!value(k)}
                      onChange={(e) => update(k, e.target.checked)}
                      disabled={isRo}
                      className="w-4 h-4"
                      data-testid={`settings-input-${k}`}
                    />
                    <span className="text-sm text-[#114232]">{prettyLabel(k)}</span>
                  </label>
                );
              }
              return (
                <div key={k} data-testid={`settings-field-${k}`}>
                  <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">
                    {prettyLabel(k)}
                    {isRo && <span className="ml-1 text-[#c15337]">(read-only)</span>}
                  </Label>
                  <Input
                    type={t === "number" ? "number" : "text"}
                    value={value(k) ?? ""}
                    disabled={isRo}
                    onChange={(e) => update(k, e.target.value)}
                    data-testid={`settings-input-${k}`}
                    className={draft[k] !== undefined ? "border-[#c15337]" : ""}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
